import {
  formatCalendarDate,
  formatCalendarDateRelative,
  isCalendarDatePast,
} from "@/lib/date";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useMemo, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumericInput } from "@/components/ui/numeric-input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DataTable, DataTableColumnDef } from "@/components/ui/data-table";
import { InvCertificateTargetEnum } from "@brewbase/db/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import {
  BeakerIcon,
  BoxesIcon,
  ClipboardPlusIcon,
  CookingPotIcon,
  Edit,
  FlaskConicalOffIcon,
  InboxIcon,
  Maximize2,
  ReceiptTextIcon,
  TagIcon,
  WaypointsIcon,
  WrenchIcon,
} from "lucide-react";
import {
  SlimTabs,
  SlimTabsList,
  SlimTabsTrigger,
} from "@/components/ui/slim-tabs";
import NewPurchasedItem from "@/features/inventory/NewPurchasedItem";
import PurchaseOrderForm from "@/features/inventory/PurchaseOrderForm";
import PrepareFromRecipe from "@/features/inventory/PrepareFromRecipe";
import PrepareDilution from "@/features/inventory/PrepareDilution";
import PrintLabelPreview from "@/features/inventory/print-label-preview";
import { InvItemId, InvLocationId } from "@brewbase/db/types";
import ItemDetailsDrawer from "@/features/inventory/ItemDetailsDrawer";
import { getSkuForItem, toCalendarDate } from "@brewbase/lib";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { z } from "zod";
import { FaPumpSoap } from "react-icons/fa";
import ReceivingWorkspace from "@/features/inventory/ReceivingWorkspace";
import {
  MoreHorizontal,
  MoveRight,
  TriangleAlert,
  Undo2,
  Trash2,
  Gauge,
  Sparkles,
} from "lucide-react";
import { DialogClose } from "@radix-ui/react-dialog";
import { Combobox } from "@/components/ui/combobox";
import { FileUploader, FileInput } from "@/components/ui/file-uploader";
import type { RowSelectionState } from "@tanstack/react-table";
import {
  EMPTY_STERILIZATION,
  SterilizationFields,
  sterilizationInput,
  type SterilizationValue,
  type SterilizeInput,
} from "@/features/inventory/sterilization-fields";

type InventoryItem = RouterOutputs["inventory"]["item"]["list"][number];

export const Route = createFileRoute("/inventory/")({
  component: InventoryPage,
  validateSearch: z.object({
    quickfiltertext: z.string().optional(),
    itemId: z.coerce
      .number()
      .transform((v) => v as InvItemId)
      .optional(),
  }),
});

function InventoryPage() {
  const navigate = Route.useNavigate();

  const [isNewPurchasedDialogOpen, setIsNewPurchasedDialogOpen] =
    useState(false);
  const [isPrepareDilutionDialogOpen, setIsPrepareDilutionDialogOpen] =
    useState(false);
  const [groupBy, setGroupBy] = useState<"none" | "resource" | "location">(
    "none",
  );
  const [drawerInitialAction, setDrawerInitialAction] = useState<
    | "ADJUST"
    | "MOVE"
    | "QUARANTINE"
    | "RELEASE"
    | "DISPOSE"
    | "STERILIZE"
    | "UPDATE_INFO"
    | "CHANGE_PRODUCT"
    | null
  >(null);
  const [activeClassTab, setActiveClassTab] = useState<
    | "ALL"
    | "INGREDIENT"
    | "FOOD_INGREDIENT"
    | "AUXILIARY_MATERIAL"
    | "CONSUMABLE_SUPPLY"
    | "TRACKED_EQUIPMENT"
  >("ALL");
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(true);
  const [selectedItems, setSelectedItems] = useState<InventoryItem[]>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const [activeBulkAction, setActiveBulkAction] = useState<
    | null
    | "MOVE"
    | "QUARANTINE"
    | "RELEASE"
    | "DISPOSE"
    | "STERILIZE"
    | "PRINT"
    | "CERT_ATTACH"
    | "UPDATE_INFO"
    | "CHANGE_PRODUCT"
  >(null);
  const [bulkNote, setBulkNote] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const [bulkSterilization, setBulkSterilization] =
    useState<SterilizationValue>(EMPTY_STERILIZATION);
  const [bulkLocationId, setBulkLocationId] = useState<string>("");
  const [bulkCertFile, setBulkCertFile] = useState<File[] | null>(null);
  const [bulkCertTypeId, setBulkCertTypeId] = useState<number | "">("");
  const [bulkExpiryDate, setBulkExpiryDate] = useState<string>("");
  const [bulkLotNumber, setBulkLotNumber] = useState<string>("");
  const [bulkProductId, setBulkProductId] = useState<string>("");
  const { quickfiltertext, itemId } = Route.useSearch();
  const utils = trpc.useUtils();

  const { data: items = [] } = trpc.inventory.item.list.useQuery({
    available_only: showOnlyAvailable ? true : undefined,
  });
  const { data: locations = [] } = trpc.inventory.location.list.useQuery();
  const { data: certificateTypes = [] } =
    trpc.inventory.certificateType.list.useQuery();
  const { data: products = [] } = trpc.inventory.product.list.useQuery();

  const sterilizeSubset = trpc.inventory.item.sterilizeSubset.useMutation({
    onSuccess: async () => {
      toast.success("Sterilized portion created");
      await utils.inventory.item.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const { mutate: markItemUnavailable, isPending: isMarkingUnavailable } =
    trpc.inventory.item.markAsUnavailable.useMutation({
      onSuccess: () => {
        utils.inventory.item.list.invalidate();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });

  const quarantineMutation = trpc.inventory.item.quarantine.useMutation({
    onSuccess: async () => {
      await utils.inventory.item.list.invalidate();
    },
  });
  const releaseMutation = trpc.inventory.item.release.useMutation({
    onSuccess: async () => {
      await utils.inventory.item.list.invalidate();
    },
  });
  const disposeMutation = trpc.inventory.item.dispose.useMutation({
    onSuccess: async () => {
      await utils.inventory.item.list.invalidate();
    },
  });
  const sterilizeMutation = trpc.inventory.item.sterilize.useMutation({
    onSuccess: async () => {
      await utils.inventory.item.list.invalidate();
    },
  });
  const moveMutation = trpc.inventory.item.move.useMutation({
    onSuccess: async () => {
      await utils.inventory.item.list.invalidate();
    },
  });

  const updateMetadataMutation =
    trpc.inventory.item.updateItemMetadata.useMutation({
      onSuccess: async () => {
        await utils.inventory.item.list.invalidate();
      },
    });

  const changeProductMutation = trpc.inventory.item.changeProduct.useMutation({
    onSuccess: async () => {
      await utils.inventory.item.list.invalidate();
    },
  });

  const runBulk = async (
    fn: (id: InvItemId) => Promise<any> | void,
    fallback?: (id: InvItemId) => void,
  ) => {
    const ids = selectedItems.map((i) => i.id as InvItemId);
    const results = await Promise.allSettled(
      ids.map(async (id) => {
        const r = fn(id);
        if (r && typeof (r as any).then === "function") return r;
        if (fallback) return fallback(id);
        return r;
      }),
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    const succeeded = results.length - failed;
    if (failed === 0) toast.success(`${succeeded} item(s) updated`);
    else toast.error(`${failed} failed, ${succeeded} succeeded`);
    setActiveBulkAction(null);
    setBulkNote("");
    setBulkReason("");
    setBulkSterilization(EMPTY_STERILIZATION);
    setBulkLocationId("");
    setBulkExpiryDate("");
    setBulkLotNumber("");
    setBulkProductId("");
    setSelectedItems([]);
    setRowSelection({});
  };

  const columns = useMemo(() => {
    const cols: DataTableColumnDef<InventoryItem, any>[] = [
      {
        id: "sku",
        header: "SKU",
        accessorFn: (row) =>
          getSkuForItem({
            inventory_resource_id: row.resource_id,
            id: row.id,
          }),
        cellTypeId: "string",
      },
      {
        id: "resource_name",
        header: "Resource",
        accessorKey: "resource_name",
        enableSorting: true,
        enableHiding: false,
        cellTypeId: "string",
        size: 300,
      },
      {
        id: "quantity",
        header: "Qty",
        accessorFn: (row) => ({
          value: row.current_quantity,
          unit: row.quantity_unit,
        }),
        cellTypeId: "numberWithUnit",
        cell: ({ row }) => {
          const it = row.original;
          if (it?.is_non_depleting) {
            return <span className="text-muted-foreground">N/A</span>;
          }
          const v = it?.current_quantity;
          const u = it?.quantity_unit;
          return (
            <span>
              {v} {u}
            </span>
          );
        },
        enableSorting: true,
        size: 160,
      },
      {
        id: "location_name",
        header: "Location",
        accessorKey: "location_name",
        enableSorting: true,
        cell: ({ row }) => row.original.location_name || "-",
      },
      {
        id: "expiry_date",
        header: "Expiry Date",
        accessorKey: "expiry_date",
        cellTypeId: "date",
        enableSorting: true,
        cell: ({ row }) => {
          if (!row.original.expiry_date) {
            return "-";
          }
          const expiry = row.original.expiry_date;
          const dateString = formatCalendarDate(expiry);
          const relativeDate = formatCalendarDateRelative(expiry);
          const isExpired = isCalendarDatePast(expiry);
          return (
            <span>
              {dateString}{" "}
              <span
                className={cn(
                  isExpired && row.original.current_quantity > 0
                    ? "text-error"
                    : "text-muted-foreground",
                )}
              >
                ({relativeDate})
              </span>
            </span>
          );
        },
        size: 300,
      },
      {
        id: "formula",
        header: "Formula",
        accessorKey: "resource_formula",
        enableSorting: true,
        cellTypeId: "string",
      },
      // {
      //   id: "concentration",
      //   header: "Concentration",
      //   accessorKey: "concentration_ww_pct",
      //   enableSorting: true,
      //   cell: ({ row }) => {
      //     const value = row.original.resource_metadata.concentration_ww_pct;
      //     return <span>{value}%</span>;
      //   },
      //   cellTypeId: "number",
      // },
      {
        id: "supplier_name",
        header: "Supplier",
        accessorKey: "supplier_name",
        enableSorting: true,
        cell: ({ row }) => row.original.supplier_name || "-",
      },
      {
        id: "lot_number",
        header: "Lot #",
        accessorKey: "lot_number",
        enableSorting: true,
        cellTypeId: "string",
      },
      {
        id: "certificates",
        header: "Certificates",
        accessorKey: "certificate_type_names",
        enableSorting: false,
        cellTypeId: "array",
        meta: {
          filterOptions: certificateTypes
            .filter((t) => t.applies_to.includes(InvCertificateTargetEnum.item))
            .map((t) => ({
              label: t.name,
              value: t.name,
            })),
        },
        size: 280,
      },
      {
        id: "available",
        header: "Available?",
        accessorKey: "available",
        enableSorting: true,
        cellTypeId: "boolean",
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row, cell }) => {
          if (cell.getIsAggregated()) {
            return null;
          }
          return (
            <div className="flex items-center gap-1">
              {row.original.current_quantity > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  title="Mark as unavailable"
                  onClick={() => {
                    if (
                      window.confirm(
                        "Are you sure you want to mark this item as unavailable?",
                      )
                    ) {
                      markItemUnavailable(row.original.id);
                    }
                  }}
                  disabled={isMarkingUnavailable}
                >
                  <FlaskConicalOffIcon className="h-4 w-4" />
                </Button>
              )}
              {row.original.current_quantity > 0 && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      title="Sterilize portion"
                    >
                      <Sparkles className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[480px]">
                    <DialogTitle>
                      Sterilize portion of{" "}
                      <span className="font-normal">
                        {getSkuForItem({
                          inventory_resource_id: row.original.resource_id,
                          id: row.original.id,
                        })}
                      </span>
                    </DialogTitle>
                    <SterilizeSubsetForm
                      item={row.original}
                      onCancel={() => {}}
                      onSubmit={async (payload) => {
                        const result = await sterilizeSubset.mutateAsync({
                          source_item_id: row.original.id as InvItemId,
                          amount: payload.amount,
                          ...payload.sterilization,
                          note: payload.note,
                          location_id: payload.location_id,
                        });
                        navigate({
                          to: "/inventory",
                          search: { itemId: result.id },
                        });
                      }}
                      pending={sterilizeSubset.isPending}
                    />
                  </DialogContent>
                </Dialog>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate({
                    to: "/inventory",
                    search: { itemId: row.original.id },
                  });
                }}
              >
                <Edit className="h-4 w-4" />
              </Button>

              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <TagIcon className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <PrintLabelPreview
                    labelType="item"
                    id={row.original.id}
                    showCancel={false}
                  />
                </DialogContent>
              </Dialog>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    title="More actions"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate({
                        to: "/inventory",
                        search: { itemId: row.original.id },
                      });
                      setDrawerInitialAction("UPDATE_INFO");
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" /> Edit lot & expiry
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate({
                        to: "/inventory",
                        search: { itemId: row.original.id },
                      });
                      setDrawerInitialAction("CHANGE_PRODUCT");
                    }}
                  >
                    <BoxesIcon className="h-4 w-4 mr-2" /> Change product
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate({
                        to: "/inventory",
                        search: { itemId: row.original.id },
                      });
                      setDrawerInitialAction("ADJUST");
                    }}
                  >
                    <Gauge className="h-4 w-4 mr-2" /> Adjust quantity
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate({
                        to: "/inventory",
                        search: { itemId: row.original.id },
                      });
                      setDrawerInitialAction("MOVE");
                    }}
                  >
                    <MoveRight className="h-4 w-4 mr-2" /> Move to location
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate({
                        to: "/inventory",
                        search: { itemId: row.original.id },
                      });
                      setDrawerInitialAction("STERILIZE");
                    }}
                  >
                    <Sparkles className="h-4 w-4 mr-2" /> Sterilize
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate({
                        to: "/inventory",
                        search: { itemId: row.original.id },
                      });
                      setDrawerInitialAction("QUARANTINE");
                    }}
                  >
                    <TriangleAlert className="h-4 w-4 mr-2" /> Quarantine
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate({
                        to: "/inventory",
                        search: { itemId: row.original.id },
                      });
                      setDrawerInitialAction("RELEASE");
                    }}
                  >
                    <Undo2 className="h-4 w-4 mr-2" /> Release
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDrawerInitialAction("DISPOSE");
                      navigate({
                        to: "/inventory",
                        search: { itemId: row.original.id },
                      });
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> Dispose
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false,
      },
    ];

    return cols;
  }, [showOnlyAvailable, certificateTypes]);

  return (
    <div className="gap-4 flex flex-col grow min-h-0 h-full">
      <div className="flex flex-col gap-3">
        {/* Quick actions + inline status */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Dialog>
              <DialogTrigger asChild>
                <Button>
                  <InboxIcon className="h-4 w-4" /> Receive Item(s)
                </Button>
              </DialogTrigger>
              <DialogContent className="pb-10 max-w-none h-[min(36rem,100vh)] w-[min(90vw,85rem)] flex flex-col gap-0">
                <DialogTitle className="flex w-full">
                  <div className="">Receive Items</div>
                  <Button
                    className="ml-auto mr-8"
                    size={"icon"}
                    variant={"ghost"}
                    onClick={() => navigate({ to: "/inventory/receiving" })}
                  >
                    <Maximize2 className="h-4 w-4" />
                  </Button>
                </DialogTitle>
                <ReceivingWorkspace asModal />
              </DialogContent>
            </Dialog>

            <Dialog
              open={isNewPurchasedDialogOpen}
              onOpenChange={setIsNewPurchasedDialogOpen}
            >
              <DialogTrigger asChild>
                <Button>
                  <ClipboardPlusIcon className="h-4 w-4 mr-1" /> New Purchased
                  Item
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[720px]">
                <NewPurchasedItem
                  asModal
                  onComplete={() => setIsNewPurchasedDialogOpen(false)}
                />
              </DialogContent>
            </Dialog>

            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <ReceiptTextIcon className="h-4 w-4 mr-1" /> Create PO
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-none w-[min(90vw,920px)]">
                <PurchaseOrderForm asModal />
              </DialogContent>
            </Dialog>

            <PrepareFromRecipeDialog />

            <Dialog
              open={isPrepareDilutionDialogOpen}
              onOpenChange={setIsPrepareDilutionDialogOpen}
            >
              <DialogTrigger asChild>
                <Button variant="outline">
                  <BeakerIcon className="h-4 w-4 mr-1" /> Prepare Dilution
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[720px]">
                <DialogTitle hidden>Prepare Dilution</DialogTitle>
                <PrepareDilution
                  asModal
                  onComplete={() => setIsPrepareDilutionDialogOpen(false)}
                />
              </DialogContent>
            </Dialog>

            <Button variant="outline" asChild>
              <Link to="/fermentations/bottles">
                <Sparkles className="h-4 w-4 mr-1" /> Prepare bottles
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <DataTable
        data={items.filter(
          (it) =>
            activeClassTab === "ALL" || it.resource_class === activeClassTab,
        )}
        columns={columns}
        selectable
        rowSelection={rowSelection}
        onRowSelectionStateChange={setRowSelection}
        onSelectionChange={(rows) =>
          setSelectedItems(rows as unknown as InventoryItem[])
        }
        getRowId={(row) => String(row.id)}
        initialSearchValue={quickfiltertext}
        searchPlaceholder="Search inventory..."
        enableGrouping={true}
        defaultGrouping={
          groupBy === "none"
            ? []
            : [groupBy === "resource" ? "resource_name" : "location_name"]
        }
        hardcodedGrouping={groupBy !== "none"}
        enableColumnResizing={true}
        maxHeight="none"
        className="grow min-h-0 flex-1"
        defaultColumnVisibility={{
          formula: false,
          supplier_name: false,
          lot_number: false,
          available: false,
        }}
        defaultFilters={
          showOnlyAvailable
            ? [
                {
                  id: "only-available",
                  columnId: "quantity",
                  operator: "greaterThan",
                  value: 0,
                },
              ]
            : []
        }
        leftCustomToolbar={
          <SlimTabs
            value={activeClassTab}
            onValueChange={(v) => setActiveClassTab(v as typeof activeClassTab)}
          >
            <SlimTabsList>
              <SlimTabsTrigger
                value="ALL"
                icon={<WaypointsIcon className="h-4 w-4" />}
              >
                All
              </SlimTabsTrigger>
              <SlimTabsTrigger
                value="INGREDIENT"
                icon={<CookingPotIcon className="h-4 w-4" />}
              >
                Ingredients
              </SlimTabsTrigger>
              <SlimTabsTrigger
                value="FOOD_INGREDIENT"
                icon={<BeakerIcon className="h-4 w-4" />}
              >
                Food
              </SlimTabsTrigger>
              <SlimTabsTrigger
                value="AUXILIARY_MATERIAL"
                icon={<FaPumpSoap className="h-4 w-4" />}
              >
                Cleaners & Reagents
              </SlimTabsTrigger>
              <SlimTabsTrigger
                value="CONSUMABLE_SUPPLY"
                icon={<BoxesIcon className="h-4 w-4" />}
              >
                Consumables
              </SlimTabsTrigger>
              <SlimTabsTrigger
                value="TRACKED_EQUIPMENT"
                icon={<WrenchIcon className="h-4 w-4" />}
              >
                Equipment
              </SlimTabsTrigger>
            </SlimTabsList>
          </SlimTabs>
        }
        rightCustomToolbar={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowOnlyAvailable(!showOnlyAvailable)}
            >
              {showOnlyAvailable ? "Show All" : "Show Only Available"}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  {groupBy === "none"
                    ? "Group"
                    : groupBy === "resource"
                      ? "Group by: Resource"
                      : "Group by: Location"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setGroupBy("none")}>
                  None
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setGroupBy("resource")}>
                  Resource
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setGroupBy("location")}>
                  Location
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      {selectedItems.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-md rounded-lg px-3 py-2 flex items-center gap-3">
          <span className="text-sm">{selectedItems.length} selected</span>
          <div className="w-px h-5 bg-border" />
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                runBulk(
                  () => {},
                  (id) => markItemUnavailable(id),
                )
              }
              disabled={isMarkingUnavailable}
            >
              Mark Unavailable
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline">
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem
                  onClick={() => setActiveBulkAction("UPDATE_INFO")}
                >
                  Edit Lot & Expiry
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setActiveBulkAction("CHANGE_PRODUCT")}
                >
                  Change Product
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setActiveBulkAction("MOVE")}>
                  Move
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setActiveBulkAction("STERILIZE")}
                >
                  Sterilize
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setActiveBulkAction("QUARANTINE")}
                >
                  Quarantine
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setActiveBulkAction("RELEASE")}
                >
                  Release
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setActiveBulkAction("DISPOSE")}
                >
                  Dispose
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              size="sm"
              variant="outline"
              onClick={() => setActiveBulkAction("CERT_ATTACH")}
            >
              Certificates
            </Button>
            <Button size="sm" onClick={() => setActiveBulkAction("PRINT")}>
              Print
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSelectedItems([]);
                setRowSelection({});
              }}
            >
              Clear Selection
            </Button>
          </div>

          {/* Bulk UPDATE_INFO */}
          <Dialog
            open={activeBulkAction === "UPDATE_INFO"}
            onOpenChange={(v) => !v && setActiveBulkAction(null)}
          >
            <DialogContent>
              <DialogTitle>
                Update info for {selectedItems.length} item(s)
              </DialogTitle>
              <div className="space-y-3">
                <div className="text-xs text-muted-foreground">
                  Leave fields blank to keep existing values
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Expiry Date</Label>
                    <Input
                      type="date"
                      value={bulkExpiryDate}
                      onChange={(e) => setBulkExpiryDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Lot Number</Label>
                    <Input
                      placeholder="Leave blank to skip"
                      value={bulkLotNumber}
                      onChange={(e) => setBulkLotNumber(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Reason (optional)</Label>
                  <Input
                    placeholder="Why are you updating this information?"
                    value={bulkReason}
                    onChange={(e) => setBulkReason(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setActiveBulkAction(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (!bulkExpiryDate && !bulkLotNumber) {
                      toast.error("Please set at least one field");
                      return;
                    }
                    runBulk((id) =>
                      updateMetadataMutation.mutateAsync({
                        id: id as InvItemId,
                        expiry_date: bulkExpiryDate
                          ? toCalendarDate(bulkExpiryDate)
                          : undefined,
                        lot_number: bulkLotNumber || undefined,
                        reason: bulkReason || undefined,
                      }),
                    );
                  }}
                  disabled={!bulkExpiryDate && !bulkLotNumber}
                >
                  Update All
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Bulk CHANGE_PRODUCT */}
          <Dialog
            open={activeBulkAction === "CHANGE_PRODUCT"}
            onOpenChange={(v) => !v && setActiveBulkAction(null)}
          >
            <DialogContent>
              <DialogTitle>
                Change product for {selectedItems.length} item(s)
              </DialogTitle>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">New Product</Label>
                  <Select
                    value={bulkProductId || undefined}
                    onValueChange={setBulkProductId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select product (or leave blank to clear)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__CLEAR__">(Clear product)</SelectItem>
                      {products.map((p) => (
                        <SelectItem key={String(p.id)} value={String(p.id)}>
                          {p.product_name || p.resource_name}{" "}
                          {p.manufacturer_name
                            ? `- ${p.manufacturer_name}`
                            : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Reason (optional)</Label>
                  <Input
                    placeholder="Why are you changing the product?"
                    value={bulkReason}
                    onChange={(e) => setBulkReason(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setActiveBulkAction(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (!bulkProductId) {
                      toast.error("Please select a product");
                      return;
                    }
                    runBulk((id) =>
                      changeProductMutation.mutateAsync({
                        id: id as InvItemId,
                        product_id:
                          bulkProductId === "__CLEAR__"
                            ? null
                            : (Number(bulkProductId) as any),
                        reason: bulkReason || undefined,
                      }),
                    );
                  }}
                  disabled={!bulkProductId}
                >
                  Change Product
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Bulk MOVE */}
          <Dialog
            open={activeBulkAction === "MOVE"}
            onOpenChange={(v) => !v && setActiveBulkAction(null)}
          >
            <DialogContent>
              <DialogTitle>Move {selectedItems.length} item(s)</DialogTitle>
              <div className="grid grid-cols-3 gap-2 items-end">
                <div className="col-span-2">
                  <Label className="text-xs">Location</Label>
                  <Select
                    value={bulkLocationId || undefined}
                    onValueChange={setBulkLocationId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((l) => (
                        <SelectItem key={String(l.id)} value={String(l.id)}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-1">
                  <Label className="text-xs">Note</Label>
                  <Input
                    value={bulkNote}
                    onChange={(e) => setBulkNote(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setActiveBulkAction(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() =>
                    runBulk((id) =>
                      moveMutation.mutateAsync({
                        id: id as InvItemId,
                        location_id: Number(
                          bulkLocationId,
                        ) as unknown as InvLocationId,
                        note: bulkNote || undefined,
                      }),
                    )
                  }
                  disabled={!bulkLocationId}
                >
                  Move All
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Bulk STERILIZE */}
          <Dialog
            open={activeBulkAction === "STERILIZE"}
            onOpenChange={(v) => !v && setActiveBulkAction(null)}
          >
            <DialogContent>
              <DialogTitle>
                Sterilize {selectedItems.length} item(s)
              </DialogTitle>
              <SterilizationFields
                value={bulkSterilization}
                onChange={setBulkSterilization}
              />
              <div>
                <Label className="text-xs">Note</Label>
                <Input
                  value={bulkNote}
                  onChange={(e) => setBulkNote(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setActiveBulkAction(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    const sterilization = sterilizationInput(bulkSterilization);
                    if (!sterilization) return;
                    runBulk((id) =>
                      sterilizeMutation.mutateAsync({
                        id,
                        ...sterilization,
                        note: bulkNote || undefined,
                      }),
                    );
                  }}
                  disabled={!sterilizationInput(bulkSterilization)}
                >
                  Sterilize All
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Bulk QUARANTINE */}
          <Dialog
            open={activeBulkAction === "QUARANTINE"}
            onOpenChange={(v) => !v && setActiveBulkAction(null)}
          >
            <DialogContent>
              <DialogTitle>
                Quarantine {selectedItems.length} item(s)
              </DialogTitle>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Reason</Label>
                  <Input
                    value={bulkReason}
                    onChange={(e) => setBulkReason(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">Note</Label>
                  <Input
                    value={bulkNote}
                    onChange={(e) => setBulkNote(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setActiveBulkAction(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() =>
                    runBulk((id) =>
                      quarantineMutation.mutateAsync({
                        id,
                        reason: bulkReason || undefined,
                        note: bulkNote || undefined,
                      }),
                    )
                  }
                >
                  Quarantine All
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Bulk RELEASE */}
          <Dialog
            open={activeBulkAction === "RELEASE"}
            onOpenChange={(v) => !v && setActiveBulkAction(null)}
          >
            <DialogContent>
              <DialogTitle>Release {selectedItems.length} item(s)</DialogTitle>
              <div>
                <Label className="text-xs">Note</Label>
                <Input
                  value={bulkNote}
                  onChange={(e) => setBulkNote(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setActiveBulkAction(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() =>
                    runBulk((id) =>
                      releaseMutation.mutateAsync({
                        id,
                        note: bulkNote || undefined,
                      }),
                    )
                  }
                >
                  Release All
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Bulk DISPOSE */}
          <Dialog
            open={activeBulkAction === "DISPOSE"}
            onOpenChange={(v) => !v && setActiveBulkAction(null)}
          >
            <DialogContent>
              <DialogTitle>Dispose {selectedItems.length} item(s)</DialogTitle>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Reason</Label>
                  <Input
                    value={bulkReason}
                    onChange={(e) => setBulkReason(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">Note</Label>
                  <Input
                    value={bulkNote}
                    onChange={(e) => setBulkNote(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="destructive"
                  onClick={() =>
                    runBulk((id) =>
                      disposeMutation.mutateAsync({
                        id,
                        reason: bulkReason || undefined,
                        note: bulkNote || undefined,
                      }),
                    )
                  }
                >
                  Dispose All
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Bulk CERT ATTACH */}
          <Dialog
            open={activeBulkAction === "CERT_ATTACH"}
            onOpenChange={(v) => !v && setActiveBulkAction(null)}
          >
            <DialogContent>
              <DialogTitle>
                Attach certificate to {selectedItems.length} item
                {selectedItems.length === 1 ? "" : "s"}
              </DialogTitle>
              <div className="flex items-end gap-2">
                <div>
                  <Label className="text-xs block mb-1">Certificate Type</Label>
                  <Combobox
                    options={certificateTypes.map((t) => ({
                      value: String(t.id),
                      label: t.name,
                    }))}
                    value={bulkCertTypeId ? String(bulkCertTypeId) : undefined}
                    onValueChange={(v?: string) =>
                      setBulkCertTypeId(v ? Number(v) : "")
                    }
                    placeholder="Select type"
                    triggerClassName="min-w-[180px]"
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-xs block mb-1">File</Label>
                  <FileUploader
                    value={bulkCertFile}
                    onValueChange={setBulkCertFile}
                  >
                    <FileInput className="border rounded-md p-2">
                      <p className="text-xs text-muted-foreground">
                        Choose file
                      </p>
                    </FileInput>
                  </FileUploader>
                </div>
                <Button
                  onClick={async () => {
                    if (
                      !bulkCertFile ||
                      bulkCertFile.length === 0 ||
                      !bulkCertTypeId
                    ) {
                      toast.error("Select type and file");
                      return;
                    }
                    const file = bulkCertFile[0];
                    const ids = selectedItems.map((i) => i.id);
                    const results = await Promise.allSettled(
                      ids.map(async (id) => {
                        const fd = new FormData();
                        fd.append("target", "item");
                        fd.append("target_id", String(id));
                        fd.append(
                          "certificate_type_id",
                          String(bulkCertTypeId),
                        );
                        fd.append("file", file);
                        const resp = await fetch("/api/certificates/upload", {
                          method: "POST",
                          body: fd,
                        });
                        if (!resp.ok) throw new Error("Upload failed");
                      }),
                    );
                    const failed = results.filter(
                      (r) => r.status === "rejected",
                    ).length;
                    const succeeded = results.length - failed;
                    if (failed === 0)
                      toast.success(`Attached to ${succeeded} items`);
                    else
                      toast.error(`${failed} failed, ${succeeded} succeeded`);
                    setActiveBulkAction(null);
                    setBulkCertFile(null);
                    setBulkCertTypeId("");
                    setSelectedItems([]);
                  }}
                  disabled={!bulkCertFile || !bulkCertTypeId}
                >
                  Attach
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Bulk PRINT */}
          <Dialog
            open={activeBulkAction === "PRINT"}
            onOpenChange={(v) => !v && setActiveBulkAction(null)}
          >
            <DialogContent className="max-w-none w-[min(90vw,720px)]">
              <DialogTitle>Print {selectedItems.length} label(s)</DialogTitle>
              <PrintLabelPreview
                labelType="item"
                ids={selectedItems.map((i) => i.id as InvItemId)}
                onComplete={() => {
                  setActiveBulkAction(null);
                  setSelectedItems([]);
                  setRowSelection({});
                }}
              />
            </DialogContent>
          </Dialog>
        </div>
      )}

      <ItemDetailsDrawer
        open={!!itemId}
        onOpenChange={() => {
          navigate({ to: "/inventory", search: { itemId: undefined } });
        }}
        itemId={itemId ?? null}
        initialAction={drawerInitialAction ?? undefined}
      />
    </div>
  );
}

function PrepareFromRecipeDialog() {
  const [isPrepareRecipeDialogOpen, setIsPrepareRecipeDialogOpen] =
    useState(false);
  const [editingItemId, setEditingItemId] = useState<InvItemId | undefined>(
    undefined,
  );

  return (
    <Dialog
      open={isPrepareRecipeDialogOpen}
      onOpenChange={setIsPrepareRecipeDialogOpen}
    >
      <DialogTrigger asChild>
        <Button variant="outline">
          <CookingPotIcon className="h-4 w-4 mr-1" /> Prepare from Recipe
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[900px] max-w-none min-w-max">
        <DialogTitle hidden>Prepare from Recipe</DialogTitle>
        <PrepareFromRecipe
          asModal
          editItemId={editingItemId}
          onCreate={(id) => setEditingItemId(id)}
          onComplete={() => setIsPrepareRecipeDialogOpen(false)}
          title="Prepare from Recipe"
        />
      </DialogContent>
    </Dialog>
  );
}

function SterilizeSubsetForm({
  item,
  onSubmit,
  onCancel,
  pending,
}: {
  item: InventoryItem;
  onSubmit: (payload: {
    amount: number;
    sterilization: SterilizeInput;
    note?: string;
    location_id?: InvLocationId;
  }) => void;
  onCancel: () => void;
  pending?: boolean;
}) {
  const [amount, setAmount] = useState<number>(0);
  const [sterilization, setSterilization] =
    useState<SterilizationValue>(EMPTY_STERILIZATION);
  const sterilizeInput = sterilizationInput(sterilization);
  const [note, setNote] = useState<string>("");
  const [locationId, setLocationId] = useState<string>("");

  const { data: locations = [] } = trpc.inventory.location.list.useQuery();

  // Preselect current item's location if present
  useEffect(() => {
    const current = item?.location_id;
    if (current != null && current !== undefined) {
      setLocationId(String(current));
    }
  }, [item]);

  const max = (item.current_quantity as number) ?? 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3 items-end">
        <div>
          <Label className="text-xs">Amount ({item.quantity_unit})</Label>
          <NumericInput
            value={amount}
            onValueChange={(v) => setAmount(v ?? 0)}
          />
          <div className="text-xs text-muted-foreground mt-1">
            Available: {item.current_quantity} {item.quantity_unit}
          </div>
        </div>
        <div className="col-span-2">
          <SterilizationFields
            value={sterilization}
            onChange={setSterilization}
          />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Location (optional)</Label>
          <Select
            value={locationId || undefined}
            onValueChange={(v) => setLocationId(v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              {locations.map((l) => (
                <SelectItem key={String(l.id)} value={String(l.id)}>
                  {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Note (optional)</Label>
          <Input
            placeholder="Optional note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <DialogClose asChild>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </DialogClose>
        <DialogClose asChild>
          <Button
            onClick={() =>
              sterilizeInput &&
              onSubmit({
                amount,
                sterilization: sterilizeInput,
                note: note || undefined,
                location_id: locationId
                  ? (Number(locationId) as unknown as InvLocationId)
                  : undefined,
              })
            }
            disabled={pending || amount <= 0 || !sterilizeInput}
          >
            Create sterilized item
          </Button>
        </DialogClose>
      </div>
    </div>
  );
}
